/**
 * Generalized content-hub filter model + tree compiler.
 *
 * Every existing filter schema (saved filter, watchlist, quick search box)
 * maps *into* `ContentHubFilter` via a small per-type adapter; only this shape
 * reaches `buildTree`, which compiles it to the declarative filter tree the
 * content-hub `POST /v1/search` body accepts.
 *
 * The tree DSL — never raw ES. A node is a leaf (field predicates) or a group
 * ({ combine, conditions, exclude }). The server rejects off-contract bodies
 * with 422.
 */
import type { ContentHubSource } from "./config";

export interface ContentHubFilter {
  /** Free text to match. Multi-word entries are treated as exact phrases. */
  keywords?: string[];
  /** How keyword terms combine. Default "or". */
  keywordsOp?: "and" | "or";
  /** Tag ids (stringify numeric ids). */
  tags?: string[];
  /** Ticker symbols (server uppercases/trims). */
  tickers?: string[];
  /** backend_source values — a view usually pins this. */
  sources?: string[];
  /** Source-defined importance strings, e.g. ["1.0"]. */
  importance?: string[];
  /** Dimensions to NOT-match (compiled into the group's `exclude`). */
  exclude?: Pick<ContentHubFilter, "keywords" | "tickers" | "tags" | "sources">;
}

/** A compiled tree node — a leaf or a group. Kept loose on purpose: the shape
 *  is validated by the server, and buildTree only ever emits DSL keys. */
export type FilterTreeNode = Record<string, unknown>;

const cleanTerms = (values?: string[]): string[] =>
  (values ?? []).map((v) => `${v}`.trim()).filter(Boolean);

const isMultiWord = (term: string): boolean => /\s/.test(term.trim());

/**
 * Compile the keyword dimension: single-word terms collapse into one `text`
 * leaf (OR within the leaf by default); each multi-word term becomes its own
 * phrase leaf. When more than one leaf results, they are wrapped in a group
 * combined per `keywordsOp`.
 */
function compileKeywords(
  keywords: string[] | undefined,
  keywordsOp: "and" | "or" | undefined
): FilterTreeNode | null {
  const terms = cleanTerms(keywords);
  if (terms.length === 0) return null;

  const singleWords = terms.filter((t) => !isMultiWord(t));
  const phrases = terms.filter(isMultiWord);

  const leaves: FilterTreeNode[] = [];
  if (singleWords.length > 0) leaves.push({ text: singleWords });
  for (const phrase of phrases) leaves.push({ text: [phrase], phrase: true });

  if (leaves.length === 1) return leaves[0];
  return {
    combine: keywordsOp === "and" ? "all" : "any",
    conditions: leaves,
  };
}

/** Compile the non-keyword dimensions of a filter into a flat list of leaves. */
function compileDimensions(
  filter: Pick<
    ContentHubFilter,
    "keywords" | "keywordsOp" | "tags" | "tickers" | "sources" | "importance"
  >
): FilterTreeNode[] {
  const nodes: FilterTreeNode[] = [];

  const keywordNode = compileKeywords(filter.keywords, filter.keywordsOp);
  if (keywordNode) nodes.push(keywordNode);

  const tags = cleanTerms(filter.tags);
  if (tags.length > 0) nodes.push({ tags });

  const tickers = cleanTerms(filter.tickers);
  if (tickers.length > 0) nodes.push({ tickers });

  const importance = cleanTerms(filter.importance);
  if (importance.length > 0) nodes.push({ importance });

  const sources = cleanTerms(filter.sources);
  if (sources.length > 0) nodes.push({ backend_source: sources });

  return nodes;
}

/**
 * Compile a `ContentHubFilter` to a content-hub filter tree.
 *
 * All dimensions are AND-ed under a top-level `combine: "all"` group; the
 * pinned source (e.g. the headlines list) is just one more `backend_source`
 * leaf AND-ed in. `exclude` dimensions compile the same way into the group's
 * `exclude` array. Empty dimensions are omitted; an all-empty filter returns
 * just the pinned-source leaf (or `{}` for a cross-source view).
 */
export function buildTree(
  filter: ContentHubFilter,
  pinnedSource?: ContentHubSource
): FilterTreeNode {
  const conditions = compileDimensions(filter);
  if (pinnedSource) {
    conditions.unshift({ backend_source: [pinnedSource] });
  }

  const exclude = filter.exclude ? compileDimensions(filter.exclude) : [];

  // No conditions at all → matches everything.
  if (conditions.length === 0 && exclude.length === 0) return {};

  // A single leaf (typically just the pinned source) needs no wrapping group,
  // but a lone exclude still needs a group to attach to.
  if (conditions.length === 1 && exclude.length === 0) return conditions[0];

  const group: FilterTreeNode = { combine: "all", conditions };
  if (exclude.length > 0) group.exclude = exclude;
  return group;
}

const sortedUnique = (values?: string[]): string[] =>
  Array.from(new Set(cleanTerms(values))).sort();

/**
 * Normalize a filter into a stable, order-independent value for React Query
 * keys. Two equivalent filters (same values, different entry order) normalize
 * equal, so they share one cache entry. Empty dimensions are dropped.
 */
export function normalizeFilter(
  filter: ContentHubFilter
): Record<string, unknown> {
  const out: Record<string, unknown> = {};

  const keywords = cleanTerms(filter.keywords);
  if (keywords.length > 0) {
    // Keyword order is significant to phrasing but not to identity — sort.
    out.keywords = [...keywords].sort();
    out.keywordsOp = filter.keywordsOp === "and" ? "and" : "or";
  }

  const tags = sortedUnique(filter.tags);
  if (tags.length > 0) out.tags = tags;

  const tickers = sortedUnique(filter.tickers);
  if (tickers.length > 0) out.tickers = tickers;

  const sources = sortedUnique(filter.sources);
  if (sources.length > 0) out.sources = sources;

  const importance = sortedUnique(filter.importance);
  if (importance.length > 0) out.importance = importance;

  if (filter.exclude) {
    const exclude = normalizeFilter(filter.exclude);
    if (Object.keys(exclude).length > 0) out.exclude = exclude;
  }

  return out;
}

/** True when no dimension of the filter carries any value. */
export function isContentHubFilterEmpty(filter: ContentHubFilter): boolean {
  return Object.keys(normalizeFilter(filter)).length === 0;
}
